import { openDB } from 'idb';

const DB_NAME = 'wtf-fm-offline';
const DB_VERSION = 1;

// Default TTL: 24 hours in milliseconds
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

// Maximum retries before marking operation as failed
const MAX_RETRIES = 5;

/**
 * Open (or create) the IndexedDB database.
 * Stores: syncQueue (pending write ops), cache (offline data cache).
 */
export async function getDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Sync queue for pending write operations
      if (!db.objectStoreNames.contains('syncQueue')) {
        const store = db.createObjectStore('syncQueue', {
          keyPath: 'id',
          autoIncrement: true,
        });
        store.createIndex('status', 'status');
        store.createIndex('created_at', 'created_at');
      }
      // Cached data for offline reading
      if (!db.objectStoreNames.contains('cache')) {
        db.createObjectStore('cache', { keyPath: 'key' });
      }
    },
  });
}

/**
 * Queue a write operation for later sync.
 * @param {Object} action - { type, method, url, data }
 *   type: descriptive label (e.g. 'sop_complete', 'pipeline_advance')
 *   method: HTTP method ('post', 'put', 'delete')
 *   url: API path (e.g. '/fm/sop/123/complete')
 *   data: request body object (optional)
 */
export async function queueAction(action) {
  const db = await getDB();
  // Dedup check: skip if identical pending operation exists
  const pending = await db.getAllFromIndex('syncQueue', 'status', 'pending');
  const isDuplicate = pending.some(
    (item) => item.type === action.type && item.url === action.url && JSON.stringify(item.data) === JSON.stringify(action.data)
  );
  if (isDuplicate) return;

  await db.add('syncQueue', {
    ...action,
    status: 'pending',
    created_at: new Date().toISOString(),
    retries: 0,
  });
}

/**
 * Process all pending sync queue items.
 * Iterates pending items, calls API via provided axiosInstance,
 * deletes on success, increments retries on failure.
 * Stops processing if device goes offline mid-sync.
 * @param {Object} axiosInst - axios instance for API calls
 * @returns {{ processed: number, succeeded: number, failed: number }}
 */
export async function processSyncQueue(axiosInst) {
  const db = await getDB();
  const pending = await db.getAllFromIndex('syncQueue', 'status', 'pending');

  let succeeded = 0;
  let failed = 0;

  for (const item of pending) {
    try {
      const method = (item.method || 'post').toLowerCase();
      if (method === 'delete') {
        await axiosInst.delete(item.url);
      } else if (method === 'get') {
        await axiosInst.get(item.url);
      } else {
        await axiosInst[method](item.url, item.data || {});
      }
      // Success - remove from queue
      await db.delete('syncQueue', item.id);
      succeeded++;
    } catch (err) {
      // If we went offline, stop processing
      if (!navigator.onLine) break;
      const newRetries = item.retries + 1;
      if (newRetries >= MAX_RETRIES) {
        // Max retries exceeded — mark as failed and stop retrying
        await db.put('syncQueue', {
          ...item,
          status: 'failed',
          retries: newRetries,
          last_error: err?.message || 'Unknown error',
          last_attempt: new Date().toISOString(),
        });
      } else {
        await db.put('syncQueue', {
          ...item,
          retries: newRetries,
          last_error: err?.message || 'Unknown error',
          last_attempt: new Date().toISOString(),
        });
      }
      failed++;
    }
  }

  return { processed: succeeded + failed, succeeded, failed };
}

/**
 * Count of items in sync queue with status='pending'.
 * @returns {number}
 */
export async function getPendingCount() {
  const db = await getDB();
  const pending = await db.getAllFromIndex('syncQueue', 'status', 'pending');
  return pending.length;
}

/**
 * Store data in the offline cache with an expiry time.
 * @param {string} key - cache key (e.g. 'kpi_gym123')
 * @param {*} data - any serializable data
 * @param {number} [ttl] - time-to-live in ms (default 24h)
 */
export async function cacheData(key, data, ttl = DEFAULT_TTL_MS) {
  const db = await getDB();
  await db.put('cache', {
    key,
    data,
    expires_at: new Date(Date.now() + ttl).toISOString(),
    cached_at: new Date().toISOString(),
  });
}

/**
 * Retrieve cached data if it exists and is not expired.
 * @param {string} key - cache key
 * @returns {*|null} cached data or null if missing/expired
 */
export async function getCachedData(key) {
  const db = await getDB();
  const entry = await db.get('cache', key);
  if (!entry) return null;
  // Check expiry
  if (new Date(entry.expires_at) < new Date()) {
    // Expired - clean up and return null
    await db.delete('cache', key);
    return null;
  }
  return entry.data;
}

/**
 * Remove all cache entries that have passed their TTL.
 */
export async function clearExpiredCache() {
  const db = await getDB();
  const tx = db.transaction('cache', 'readwrite');
  const store = tx.objectStore('cache');
  let cursor = await store.openCursor();
  const now = new Date();

  while (cursor) {
    if (new Date(cursor.value.expires_at) < now) {
      await cursor.delete();
    }
    cursor = await cursor.continue();
  }

  await tx.done;
}
